import * as React from 'react';
import { useMemo, useState } from 'react';
import * as R from 'ramda';
import LocalDiv from '../../localization/LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import { COLORS } from '../../globalStyle/colors';
import { css } from '@emotion/core';
import { FeedbackSurveyQuestion } from './models';
import { DevPlanItem } from '../devPlan/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench, faTrash, faMinusSquare, faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import ActionIcon from '../../layout/ActionIcon';
import { useAppState } from '../../core/redux/hooks';
import { Z_INDEX } from '../../globalStyle/zIndex';


const styles = {
  competency: css`
    justify-content: space-between;
    min-height: 28px;
  `,
  hide: css`
    display: none;
  `,
  question: css`
    justify-content: space-between;
  `,
  greenBg: css`
    background-color: rgba(210, 232, 190, 1); /* based on ${COLORS.LIGHT_GREEN} */
  `,
  lightGreenBg: css`
    background-color: rgba(210, 232, 190, .5); /* based on ${COLORS.LIGHT_GREEN} */
  `,
  blueBg: css`
    background-color: rgba(143, 193, 255, 1); /* based on ${COLORS.LIGHT_BLUE} */
  `,
  lightBlueBg: css`
    background-color: rgba(143, 193, 255, .5); /* based on ${COLORS.LIGHT_BLUE} */
  `,
  borderTop: css `
    border-top: 1px solid ${COLORS.WHITE};
  `,
  questionText: css`
    padding: 7px 26px;
    overflow: auto;
  `,
  wrenchIcon: css`
    margin-top: 7px;
  `,
  competencyOpen: css `
    margin-top: 5px;
  `,
  competencyName: css `
    margin: 7px 10px 0;
  `,
  deleteBtn:      css`
    border: none;
    background-color: transparent;
    text-decoration: none;
    color: ${COLORS.DARK_GREY};
    margin-top: 5px;

    &:hover { 
      opacity: .6;
      cursor:pointer;
    }
  `,
  deleteIcon:     css`
    font-size: 16px;
    margin-bottom: 2px;
  `,
  iconWhite: css`
    color: ${COLORS.WHITE};
  `,
  iconDarkGrey: css`
    color: ${COLORS.DARK_GREY};
  `,
  plusMinus: css`
    font-size: 1.1em;
    cursor: pointer;
    text-align: center;
    color: ${COLORS.DARK_GREY};
  `,
  plusMinusDarkBg: css`
    font-size: 1.1em;
    cursor: pointer;
    text-align: center;
    color: ${COLORS.WHITE};
  `,  center: css`
    justify-content: center;
  `,
  padEnds: css`
    padding: 0 14px;
  `,
  italicized: css`
    font-style: italic;
  `,
};


type Props = {
  className?: string;
  questions: FeedbackSurveyQuestion[];
  devItems: DevPlanItem[];
  addByCompetency: boolean;
  showDelete: boolean;
  deleteQuestion: (questionId: number) => void;
  deleteQuestionsForCompetency: (competencyId: number) => void;
  darkBackground: boolean;
  addedStyle: boolean;
  createdMode: boolean;
};


/**
 * CreateSurveyViewAccordian: a component that shows subset of questions
 * PureComponent: true
 * Redux Connected: false
 */
const CreateSurveyViewAccordian: React.FC<Props> = props => {
  const competencies = Array.from(new Set(props.questions.map((item: FeedbackSurveyQuestion) => item.competencyId)))
  const [open, setOpen] = useState<number | undefined>(undefined);
  const menuOpen = useAppState(state => state.openSideMenu);    

  const accordian = useMemo(
    () => { 
      const showWrench = (competencyId: number) => (
        competencyId > 0 && props.devItems.some(x => x.devItemCompetencyId === competencyId)
      );
    
      const questionSelected = (questionId: number) => (
        props.questions.some(x => x.questionId === questionId)
      );
    
      const competencySelected = (competencyId: number) => (
        props.questions.some(x => x.competencyId === competencyId)
      );
    
      const questionRequired = (questionId: number) => (
        props.questions.some(x => x.questionId === questionId && x.required)
      );
    
      const competencyRequired = (competencyId: number) => (
        props.questions.some(x => x.competencyId === competencyId && x.required)
      );

      const wrenchColor = () => (
        props.darkBackground
          ? styles.iconWhite
          : styles.iconDarkGrey
      );

      const showDeleteCompetency = (props.showDelete && props.addByCompetency);
      const showDeleteQuestion = (props.showDelete && !props.addByCompetency);
    
      const addedStyle = (competencyId: number) => (
        props.addedStyle
        ? [
            styles.padEnds,
            open === competencyId
              ? (props.createdMode ? styles.lightBlueBg : styles.lightGreenBg)
              : (props.createdMode ? styles.blueBg : styles.greenBg),
            styles.borderTop,
          ]
        : undefined
      );

      const openStyle = (competencyId: number) => (
        competencyId === 0 ? styles.italicized : undefined
      );

      const competency = (competencyId: number) => {
        const question = props.questions.find(q => q.competencyId === competencyId);

        return(
          <LocalDiv key={competencyId} css={[FLEX_ROW, styles.competency, addedStyle(competencyId)]}>
            <LocalDiv css={FLEX_COLUMN}>
              <LocalDiv css={FLEX_ROW}>
                <ActionIcon
                  css={
                    [FLEX_COLUMN, styles.competencyOpen, css`z-index: ${menuOpen ? 'auto' : Z_INDEX.ABOVE_SIDE_MENU}`]
                  }
                  onClick={
                    open === competencyId
                      ? () => setOpen(undefined)
                      : () => setOpen(competencyId)
                  }
                  icon={open === competencyId ? faMinusSquare : faPlusSquare}
                  iconStyle={props.darkBackground ? styles.plusMinusDarkBg : styles.plusMinus}
                  type='button'
                />
                <span css={[FLEX_COLUMN, styles.competencyName, openStyle(competencyId)]}>
                  {question?.competencyName}
                </span>
                {
                  showWrench(competencyId || 0) && (
                    <FontAwesomeIcon
                      icon={faWrench}
                      css={[FLEX_COLUMN, styles.wrenchIcon, wrenchColor()]}
                      size={'1x'}
                    />
                  )
                }
              </LocalDiv>
            </LocalDiv>
            {
              showDeleteCompetency && competencySelected(competencyId) && !competencyRequired(competencyId) && (
                <LocalDiv css={[FLEX_COLUMN, styles.center]}>
                  <button 
                    onClick={() => props.deleteQuestionsForCompetency(competencyId)}
                    css={styles.deleteBtn}
                  >
                    <FontAwesomeIcon icon={faTrash} css={styles.deleteIcon}/>
                  </button>
                </LocalDiv>
              )
            }        
          </LocalDiv>
        )
      };
    
      const questions = (competencyId: number) => {
        const questionsForCompetency = props.questions.filter(q => q.competencyId === competencyId)
    
        return (
          <LocalDiv 
            css={open !== competencyId && styles.hide}
          >
            {questionMap(questionsForCompetency)}
          </LocalDiv>
        )
      };
      
      const questionKey = (question: FeedbackSurveyQuestion) => (
        `${question.competencyId}|${question.questionId}`
      );
    
      const questionMap = (questionsForCompetency: FeedbackSurveyQuestion[]) => (
        R.map(
          (question: FeedbackSurveyQuestion) => (
            <LocalDiv key={questionKey(question)} css={[FLEX_ROW, styles.question, addedStyle(question.competencyId)]}>
              <LocalDiv css={[FLEX_COLUMN, styles.questionText]}>
                {question.questionText}
              </LocalDiv>
              {
                showDeleteQuestion && questionSelected(question.questionId)
                  && !questionRequired(question.questionId) && (
                  <LocalDiv css={[FLEX_COLUMN, styles.center]}>
                    <button 
                      onClick={() => props.deleteQuestion(question.questionId)}
                      css={styles.deleteBtn}
                    >
                      <FontAwesomeIcon icon={faTrash} css={styles.deleteIcon}/>
                    </button>
                  </LocalDiv>
                )
              }
            </LocalDiv>
          ),
          questionsForCompetency,
        )
      ); 

      return R.map(
        (competencyId: number) => (
          <LocalDiv key={`|${competencyId}|`}>
            {competency(competencyId)}
            {questions(competencyId)}
          </LocalDiv>
        ),
        competencies,
      )
    },
    [
      competencies,
      open,
      props,
      menuOpen,
    ],
  );

  return (
    <>
      {accordian}
    </>
  );
};

export default CreateSurveyViewAccordian;
